'use client';

import Image from 'next/image';
import { montserratSemi } from '../fonts';
import { Link } from '@/i18n/routing';

interface AppButtonProps {
  text?: string;
  icon?: string;
  href?: string;
  iconHover?: string; // For buttons with two icon states (hover effect)
  iconAlt?: string;
  variant?: 'filled' | 'outline' | 'transparent';
  size?: 'sm' | 'md' | 'lg';
  iconPosition?: 'left' | 'right';
  iconOnly?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  style?: React.CSSProperties;
  noMinDimensions?: boolean; // For mobile flexibility
}

export default function AppButton({
  text,
  icon,
  iconHover,
  iconAlt = 'icon',
  variant = 'filled',
  size = 'md',
  iconPosition = 'right',
  iconOnly = false,
  fullWidth = false,
  onClick,
  href,
  type = 'button',
  className = '',
  style = {},
  noMinDimensions = false,
}: AppButtonProps) {
  // Size configurations
  const sizeConfig = {
    sm: {
      text: 'text-xs',
      padding: iconOnly ? 'px-2 py-2' : 'px-4 py-2',
      iconSize: 22,
      minWidth: noMinDimensions ? undefined : iconOnly ? '40px' : '152px',
      minHeight: noMinDimensions ? undefined : iconOnly ? '40px' : '44px',
    },
    md: {
      text: 'text-sm',
      padding: iconOnly ? 'px-2 py-2' : 'px-4 py-2',
      iconSize: 24,
      minWidth: noMinDimensions ? undefined : iconOnly ? '50px' : '156px',
      minHeight: noMinDimensions ? undefined : iconOnly ? '50px' : '50px',
    },
    lg: {
      text: 'text-base',
      padding: iconOnly ? 'px-2 py-2' : 'px-8 py-4',
      iconSize: 26,
      minWidth: noMinDimensions ? undefined : iconOnly ? '70px' : '160px',
      minHeight: noMinDimensions ? undefined : iconOnly ? '70px' : '56px',
    },
  };

  const config = sizeConfig[size];

  // Variant styles
  const variantStyles = {
    filled: 'bg-[var(--background)] border-2 border-[var(--background)] text-[var(--text-primary)] hover:shadow-[0_15px_30px_rgba(255,255,255,0.35)]',
    outline: 'bg-transparent border-2 border-[var(--text-primary)] text-[var(--text-primary)] hover:bg-[var(--text-primary)] hover:text-[var(--background)] hover:shadow-[0_15px_30px_rgba(255,255,255,0.35)]',
    transparent: 'bg-[var(--transparent)] border-2 border-[var(--main-color)] text-[var(--main-color)] hover:bg-[var(--main-color)] hover:text-[var(--background)] hover:shadow-[0_15px_30px_rgba(11,113,255,0.35)]',
  };

  // Icon container with hover effect support
  const renderIcon = () => {
    if (!icon) return null;

    const iconContainerStyle: React.CSSProperties = {
      width: config.iconSize,
      height: config.iconSize,
    };

    if (iconHover) {
      // Two-state icon (e.g., mobile-w.svg and mobile.svg)
      return (
        <span className="relative inline-flex items-center justify-center" style={iconContainerStyle}>
          <Image
            src={icon}
            alt={iconAlt}
            width={config.iconSize}
            height={config.iconSize}
            className="absolute inset-0 transition-transform duration-300 origin-center scale-100 group-hover:scale-0"
          />
          <Image
            src={iconHover}
            alt={`${iconAlt} hover`}
            width={config.iconSize}
            height={config.iconSize}
            className="absolute inset-0 scale-0 group-hover:scale-100 transition-transform duration-300 origin-center"
          />
        </span>
      );
    }

    // Single icon
    return (
      <span className="relative inline-flex items-center justify-center" style={iconContainerStyle}>
        <Image
          src={icon}
          alt={iconAlt}
          width={config.iconSize}
          height={config.iconSize}
          className="absolute inset-0 transition-transform duration-300 origin-center"
        />
      </span>
    );
  };

  // Determine if button should be square (icon only)
  const isIconOnly = iconOnly || (!text && icon);
  const buttonStyle: React.CSSProperties = {
    ...style,
    ...(config.minWidth && !noMinDimensions ? { minWidth: config.minWidth } : {}),
    ...(config.minHeight && !noMinDimensions ? { minHeight: config.minHeight } : {}),
    ...(isIconOnly && config.minWidth && config.minHeight && !noMinDimensions
      ? { width: config.minWidth, height: config.minHeight }
      : {}),
    ...(fullWidth ? { width: '100%' } : {}),
  };

  const buttonClasses = `${montserratSemi.className} rounded-full ${config.padding} ${config.text} transition-all duration-300 hover:brightness-110 group flex items-center justify-center cursor-pointer ${variantStyles[variant]} ${className}`;
  const content = (
    <>
      {text && iconPosition === 'left' && icon && (
        <span className={isIconOnly ? '' : 'mr-4 inline-flex items-center justify-center'}>
          {renderIcon()}
        </span>
      )}
      {text && <span>{text}</span>}
      {text && iconPosition === 'right' && icon && (
        <span className={isIconOnly ? '' : 'ml-4 inline-flex items-center justify-center'}>
          {renderIcon()}
        </span>
      )}
      {isIconOnly && icon && renderIcon()}
    </>
  );

  if (href) {
    return (
      <Link href={href} className={buttonClasses} style={buttonStyle}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonClasses}
      style={buttonStyle}
    >
      {content}
    </button>
  );
}

